import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, X, History, Info, BookOpen,
  ClipboardCheck, AlertTriangle, CheckCircle2, Settings, Tag, ShieldCheck,
} from 'lucide-react';
import {
  getChefWeeks, getChefRecipes, assignDayRecipe, unassignDayRecipe, getRecipes, getRecipe, deleteRecipe,
  getGuidelineCheck,
  getScenarioBoardMap, upsertScenarioBoardMapDay, deleteScenarioBoardMapDay, deleteScenarioBoardMapScenario, setBoardOverride,
  getVegSpecies, createVegSpecies, updateVegSpecies, deleteVegSpecies,
  getFoodClassification, setFoodClassification, getClassificationList,
  getScoopMlOverview, updateScoopMlOverview, getScoopWeightSuggestion,
  approveWarning, revokeWarningApproval,
  getSearchLog,
} from '../api';
import RecipeEditor from './RecipeEditor';
import { FoodAutocomplete, SCOOP_SIZES } from './FoodPicker';

// Chef's weekly recipe planner (v1). A calendar week, Monday-Sunday — the SAME grid as the
// existing Week Schedule tab, not a separate Matchday-relative concept. The MD label/Bord shown
// on a day comes straight from Week Schedule's OWN planned data (weekly_schedule/schedule_day,
// read by server.staging.js's chef recipe routes) — only the days Week Schedule itself has
// classified as matchday-related (MD-4..MD+3) get a label/Bord; a day Week Schedule has no plan
// for at all reads "Unscheduled" there, and the same here, styled the same light-grey way.
// A recipe (nutrition_recipes) is a reusable LIBRARY entry belonging to one Bord/MD label, not a
// specific date — the chef picks from recipes already saved for a day's label instead of
// retyping one every time that label recurs (RecipePicker below), or creates a new one
// (RecipeEditor.js) which gets assigned to the day it was created from automatically.
// Used two ways:
//   - chef role, own Nutrition tab: readOnly=false, full assign/create/edit/unassign.
//   - admin role, "Chef" toggle on the existing Nutrition screen: readOnly=true (view-only, per
//     the brief — whether admin should also be able to edit is flagged as an open question in
//     the write-up, not decided here).
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Pure UTC arithmetic on 'YYYY-MM-DD' strings — reading a local-midnight Date back through
// toISOString() lands on the previous day in CET/CEST.
function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
function mondayOf(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  const day = d.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(dateStr, diff);
}
// Local calendar date — toISOString() gives the UTC date (yesterday until 01:00/02:00 NL time).
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function fmtDate(dateStr) {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}
function fmtWeekRange(weekStart) {
  const end = addDays(weekStart, 6);
  const opts = { day: '2-digit', month: 'short' };
  return `${fmtDate(weekStart)} – ${new Date(`${end}T00:00:00`).toLocaleDateString('en-GB', { ...opts, year: 'numeric' })}`;
}

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 12 };
const btn = (active, color = '#3b82f6') => ({
  padding: '6px 10px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
// Same light-grey treatment Week Schedule gives an "off"/unscheduled day (see
// WeekSchedule.js's dayColumnCells: isOffDay -> rgba(148,163,184, .12/.3)).
const UNSCHEDULED_CARD = { background: 'rgba(148,163,184,0.12)', borderColor: 'rgba(148,163,184,0.3)' };
const ghostBtn = { padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' };

// ── "Maaltijdkeuze – Chef overzicht" reference (source: the guideline sheet the chef works
// from) — condensed to what a chef needs at a glance while planning, not a full copy of the
// sheet: examples-per-family and the duplicate "Eiwit per week" recap on the sheet are dropped
// since the family names and the per-source cards already cover that. Collapsed by default,
// same interaction as "Previous weeks" below, so it stays a secondary reference next to the
// recipe planning rather than competing with it for attention.
const VEG_FAMILIES = [
  { name: 'Schermbloemigen', examples: 'Wortel, Bleekselderij, Knolselderij, Pastinaak' },
  { name: 'Koolfamilie', examples: 'Broccoli, Spruitjes, Paksoi, Boerenkool, Bloemkool, Spitskool' },
  { name: 'Peulvruchten', examples: 'Sperzieboon, Peultjes, Erwt, Linzen, Kikkererwten, Bonen' },
  { name: 'Nachtschade', examples: 'Paprika, Tomaat, Aubergine' },
  { name: 'Komkommerfamilie', examples: 'Courgette, Pompoen, Komkommer' },
  { name: 'Paddenstoelen', examples: 'Champignons, Oesterzwam, Shiitake' },
  { name: 'Bladgroentes', examples: 'Spinazie, Andijvie' },
];
const VEG_PER_BOARD = [['Bord 2', '200 g'], ['Bord 3', '100 g'], ['Bord 5', '200 g'], ['Bord 6', '60–70 g'], ['Bord 7', '60–70 g']];
// Lower end of the "min. p.p." range above — mirrors server.staging.js's VEG_PER_BOARD_MIN_G,
// used only to color the 🥦 badge (below vs. at/above the guideline), never to decide pass/fail
// itself (the guideline-check endpoint is the source of truth for that).
const VEG_PER_BOARD_MIN_G = { 'Bord 2': 200, 'Bord 3': 100, 'Bord 5': 200, 'Bord 6': 60, 'Bord 7': 60 };
const PROTEIN_GUIDELINES = [
  { label: 'Vis', freq: '1–2x', note: 'zalm, kabeljauw, tonijn' },
  { label: 'Tofu/tempeh (met kip)', freq: '1x', note: null },
  { label: 'Rood vlees', freq: '1x', note: 'rund, lam, biefstuk' },
  { label: 'Kip', freq: '2–3x', note: 'filet, dij, gehakt' },
];

function ChefMealGuidelines() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setOpen(o => !o)}>
        <Info size={13} /> Meal guidelines
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', right: 0, zIndex: 20, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, width: 340, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Groenten</div>
          <ul style={{ margin: '0 0 8px', paddingLeft: 16, fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            <li>Min. 3 soorten per maaltijd, uit verschillende families (ui en tauge telt niet mee)</li>
            <li>Max. 2x dezelfde groente in de week; streef naar 20 verschillende per week</li>
            <li>Alle plantenfamilies min. 1x gebruiken in de week</li>
          </ul>
          <div style={{ marginBottom: 10 }}>
            {VEG_FAMILIES.map(f => (
              <div key={f.name} style={{ fontSize: 11, marginBottom: 3 }}>
                <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{f.name}:</span>{' '}
                <span style={{ color: 'var(--text-muted)' }}>{f.examples}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 3, color: 'var(--text-secondary)' }}>Groenten per Bord (min. p.p.)</div>
            {VEG_PER_BOARD.map(([b, g]) => (
              <div key={b} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
                <span>{b}</span><span>{g}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, paddingTop: 10, borderTop: '1px solid var(--border)' }}>Eiwit — per week</div>
          {PROTEIN_GUIDELINES.map(p => (
            <div key={p.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, color: 'var(--text-secondary)', marginBottom: 3 }}>
              <span>{p.label}{p.note && <span style={{ color: 'var(--text-dimmed)' }}> ({p.note})</span>}</span>
              <span style={{ fontWeight: 600, flexShrink: 0 }}>{p.freq}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const modalInput = { padding: '7px 9px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit', flex: 1, boxSizing: 'border-box', outline: 'none' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modalBox = { background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxHeight: '88vh', overflowY: 'auto', padding: 18 };

// Popup showing which "Maaltijdkeuze – Chef overzicht" rules aren't met, for one day or the
// whole week — fetched fresh every time it opens (never cached) so it always reflects the
// latest recipe edits. Per-meal rules (veg variety, veg-per-Bord minimum) come back per day;
// per-week rules (family coverage, max 2x/species, protein frequency) come back once for the
// whole week and are only shown for the week-scoped check.
function GuidelineModal({ weekStart, scope, date, onClose }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    setResult(null);
    setError('');
    getGuidelineCheck(weekStart).then(setResult).catch(e => setError(e.message || 'Check mislukt.'));
  }, [weekStart]);

  const days = result ? (scope === 'day' ? result.days.filter(d => d.date === date) : result.days) : [];
  const dayIssues = days.filter(d => d.issues.length);
  const weekIssues = scope === 'week' ? (result?.week_issues || []) : [];
  const hasAnyIssue = dayIssues.length > 0 || weekIssues.length > 0;
  const hasUnapproved = dayIssues.some(d => d.issues.some(i => !i.approved)) || weekIssues.some(i => !i.approved);

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalBox, maxWidth: 440 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ClipboardCheck size={16} /> Richtlijnen — {scope === 'day' ? fmtDate(date) : 'deze week'}
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {error && <div style={{ fontSize: 12, color: '#ef4444' }}>{error}</div>}
        {!result && !error && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Checken…</div>}
        {result && !hasAnyIssue && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#10b981', fontSize: 13, fontWeight: 600 }}>
            <CheckCircle2 size={16} /> Alle richtlijnen gehaald.
          </div>
        )}
        {result && hasAnyIssue && !hasUnapproved && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#3b82f6', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
            <ShieldCheck size={15} /> Alle openstaande punten zijn door een admin goedgekeurd.
          </div>
        )}
        {result && weekIssues.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Deze week</div>
            {weekIssues.map((issue, i) => <IssueLine key={i} issue={issue} />)}
          </div>
        )}
        {dayIssues.map(d => (
          <div key={d.date} style={{ marginBottom: 10 }}>
            {scope === 'week' && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>{fmtDate(d.date)}</div>}
            {d.issues.map((issue, i) => <IssueLine key={i} issue={issue} showRecipeName={scope === 'week'} />)}
          </div>
        ))}
      </div>
    </div>
  );
}

// One warning line, shared by GuidelineModal and WarningApprovalsPanel — red when unapproved,
// blue with a shield icon and "goedgekeurd door ... op ..." when an admin has overridden it.
function IssueLine({ issue, showRecipeName }) {
  const color = issue.approved ? '#3b82f6' : '#f59e0b';
  return (
    <div style={{ display: 'flex', gap: 6, fontSize: 12, color, marginBottom: 5, alignItems: 'flex-start' }}>
      {issue.approved ? <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 1 }} /> : <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />}
      <span>
        {showRecipeName && issue.recipe_name && <strong>{issue.recipe_name}: </strong>}
        {issue.message}
        {issue.approved && (
          <span style={{ color: 'var(--text-dimmed)' }}> — goedgekeurd door {issue.approved_by || 'admin'} op {new Date(issue.approved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        )}
      </span>
    </div>
  );
}

// Admin-only: which Bord goes with which MD-offset, PER training scenario (1A, 2B, 6A, ...) —
// replaces the old flat MD-label->board table, since the same MD-label can now mean a different
// board depending on which team scenario is active that week (nutrition_scenario_board_map;
// see computeBoardForDate in server.staging.js). Dish-first-style UX, mirrors ScoopGramsEditor
// below: pick a scenario from a dropdown (or type a brand new scenario code), then edit/add/
// remove that scenario's day rows. A day's actual board shown in the week grid also factors in
// that specific date's one-off override, if any — set directly on the day card (DayColumn),
// never here.
function ScenarioBoardMapEditor({ user, onClose }) {
  const [scenarios, setScenarios] = useState(null);
  const [selected, setSelected] = useState('');
  const [newScenario, setNewScenario] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newBoard, setNewBoard] = useState('');
  const [edits, setEdits] = useState({}); // id -> board being edited
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => { getScenarioBoardMap().then(setScenarios).catch(() => setScenarios([])); }, []);
  useEffect(() => { load(); }, [load]);

  const current = scenarios && scenarios.find(s => s.scenario === selected);

  const saveRow = async (id, mdLabel, board, sortOrder) => {
    setBusy(id); setMsg(null);
    try {
      await upsertScenarioBoardMapDay(selected, mdLabel, board, sortOrder, user?.role, user?.id);
      setMsg({ ok: true, text: 'Opgeslagen.' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Opslaan mislukt.' });
    } finally { setBusy(null); }
  };

  const removeRow = async (id) => {
    setBusy(id); setMsg(null);
    try { await deleteScenarioBoardMapDay(id, user?.role); load(); }
    catch (e) { setMsg({ ok: false, text: e.message || 'Verwijderen mislukt.' }); }
    finally { setBusy(null); }
  };

  const removeScenario = async () => {
    if (!selected || !window.confirm(`Scenario "${selected}" volledig verwijderen?`)) return;
    setBusy('scenario'); setMsg(null);
    try { await deleteScenarioBoardMapScenario(selected, user?.role); setSelected(''); load(); }
    catch (e) { setMsg({ ok: false, text: e.message || 'Verwijderen mislukt.' }); }
    finally { setBusy(null); }
  };

  const addDay = async () => {
    const scenario = (selected || newScenario).trim();
    const mdLabel = newLabel.trim();
    const board = newBoard.trim();
    if (!scenario || !mdLabel || !board) return;
    setBusy('new'); setMsg(null);
    try {
      const sortOrder = current ? current.days.length + 1 : 1;
      await upsertScenarioBoardMapDay(scenario, mdLabel, board, sortOrder, user?.role, user?.id);
      setSelected(scenario); setNewScenario(''); setNewLabel(''); setNewBoard('');
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Opslaan mislukt.' });
    } finally { setBusy(null); }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalBox, maxWidth: 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Scenario-bordkoppeling</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 12 }}>
          Per scenario (1A, 2B, 6A, ...) het bord per dagtype (MD-2, MD+1, ...). Welk scenario een
          week daadwerkelijk heeft, komt uit de bestaande trainingsbelasting-kalender — hier stel
          je alleen in welk bord bij welk dagtype hoort binnen elk scenario.
        </div>
        {!scenarios && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
        {scenarios && (
          <select value={selected} onChange={e => setSelected(e.target.value)} style={{ ...modalInput, width: '100%', marginBottom: 10, display: 'block' }}>
            <option value="">— kies een scenario —</option>
            {scenarios.map(s => <option key={s.scenario} value={s.scenario}>{s.scenario}</option>)}
          </select>
        )}
        {current && current.days.map(d => (
          <div key={d.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, width: 56, flexShrink: 0 }}>{d.md_label}</span>
            <input value={edits[d.id] ?? d.board} onChange={e => setEdits(x => ({ ...x, [d.id]: e.target.value }))} style={modalInput} />
            <button style={{ ...btn(false), padding: '5px 9px', flexShrink: 0 }} disabled={busy === d.id}
              onClick={() => saveRow(d.id, d.md_label, (edits[d.id] ?? d.board).trim(), d.sort_order)}>
              {busy === d.id ? '…' : 'Save'}
            </button>
            <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', flexShrink: 0 }} disabled={busy === d.id} onClick={() => removeRow(d.id)}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {current && (
          <button style={{ ...ghostBtn, color: '#ef4444', marginBottom: 10 }} disabled={busy === 'scenario'} onClick={removeScenario}>
            Scenario "{selected}" volledig verwijderen
          </button>
        )}
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 6 }}>
            {selected ? `Dag toevoegen aan "${selected}":` : 'Nieuw scenario starten (typ een code + eerste dag):'}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {!selected && (
              <input placeholder="scenario, bv. 2E" value={newScenario} onChange={e => setNewScenario(e.target.value)} style={{ ...modalInput, maxWidth: 110 }} />
            )}
            <input placeholder="MD-label, bv. MD-2" value={newLabel} onChange={e => setNewLabel(e.target.value)} style={{ ...modalInput, maxWidth: 110 }} />
            <input placeholder="Bord, bv. Bord 5" value={newBoard} onChange={e => setNewBoard(e.target.value)} style={{ ...modalInput, maxWidth: 110 }} />
            <button style={{ ...btn(true), padding: '5px 9px' }} disabled={busy === 'new'} onClick={addDay}>
              {busy === 'new' ? '…' : <Plus size={14} />}
            </button>
          </div>
        </div>
        {msg && <div style={{ fontSize: 11, color: msg.ok ? '#10b981' : '#ef4444', marginTop: 10 }}>{msg.text}</div>}
      </div>
    </div>
  );
}

// One calendar date's one-off board correction — always wins over the scenario-derived value for
// that date, never touches the scenario definition itself. Small popover opened from a day card.
function BoardOverrideEditor({ date, currentBoard, overrideBoard, onClose, onSaved }) {
  const [value, setValue] = useState(overrideBoard || currentBoard || '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const save = async () => {
    setSaving(true); setMsg(null);
    try { await setBoardOverride(date, value.trim() || null); onSaved(); onClose(); }
    catch (e) { setMsg(e.message || 'Opslaan mislukt.'); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true); setMsg(null);
    try { await setBoardOverride(date, null); onSaved(); onClose(); }
    catch (e) { setMsg(e.message || 'Opslaan mislukt.'); }
    finally { setSaving(false); }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalBox, maxWidth: 320 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Bord voor {date}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginBottom: 8 }}>
          Eenmalige correctie voor alleen deze dag — verandert het scenario zelf niet.
        </div>
        <input value={value} onChange={e => setValue(e.target.value)} style={{ ...modalInput, width: '100%', marginBottom: 8 }} placeholder="bv. Bord 5" />
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ ...btn(true), padding: '5px 9px' }} disabled={saving} onClick={save}>{saving ? '…' : 'Save'}</button>
          {overrideBoard && (
            <button style={ghostBtn} disabled={saving} onClick={clear}>Terug naar scenario</button>
          )}
        </div>
        {msg && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 8 }}>{msg}</div>}
      </div>
    </div>
  );
}

// One approvable warning row — same red/blue "unapproved vs. admin-approved" treatment used
// throughout the grid, plus the actual Goedkeuren/Intrekken action (the only place these
// actions live — the grid and GuidelineModal are read-only displays of the same data).
function ApprovalRow({ warningType, scopeKey, message, context, approved, approvedBy, approvedAt, user, onChanged }) {
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    setBusy(true);
    try { await approveWarning(warningType, scopeKey, message, user?.role, user?.id); onChanged(); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await revokeWarningApproval(warningType, scopeKey, user?.role); onChanged(); }
    finally { setBusy(false); }
  };
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
      padding: '8px 10px', borderRadius: 8, marginBottom: 6,
      background: approved ? 'rgba(59,130,246,0.08)' : 'rgba(239,68,68,0.08)',
      border: `1px solid ${approved ? '#3b82f6' : '#ef4444'}`,
    }}>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
        {context && <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginBottom: 2 }}>{context}</div>}
        <div style={{ color: approved ? '#3b82f6' : '#ef4444', display: 'flex', alignItems: 'flex-start', gap: 5 }}>
          {approved ? <ShieldCheck size={12} style={{ flexShrink: 0, marginTop: 2 }} /> : <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />}
          <span>{message}</span>
        </div>
        {approved && (
          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 3 }}>
            Goedgekeurd door {approvedBy || 'admin'} op {new Date(approvedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
      <button style={{ ...btn(false, approved ? '#3b82f6' : '#3b82f6'), padding: '4px 9px', flexShrink: 0 }} disabled={busy} onClick={approved ? revoke : approve}>
        {busy ? '…' : approved ? 'Intrekken' : 'Goedkeuren'}
      </button>
    </div>
  );
}

// Admin-only: every current guideline violation and role-mismatch, in one place, with a
// Goedkeuren action — for a warning that's real but acceptable (a guideline that can't be met
// because a food isn't in the database, or a deliberate chef choice), WITHOUT changing any
// underlying recipe/classification/guideline data. Two sections: richtlijnen (scanned across
// every week that has any recipes assigned — weeks are typically few per season, so an N-request
// fan-out here is a fine trade for actually catching "all current" warnings) and rol-mismatches
// (library-wide via GET /api/nutrition/recipes, so it also catches a mismatch on a recipe that
// was never assigned to any day and would otherwise never appear in a week scan at all).
function WarningApprovalsPanel({ user, onClose }) {
  const [guidelineRows, setGuidelineRows] = useState(null);
  const [roleRows, setRoleRows] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setGuidelineRows(null);
    setError('');
    getChefWeeks().then(async (weeks) => {
      const recent = weeks.slice(0, 20); // safety cap — see file-header note above
      const checks = await Promise.all(recent.map(w => getGuidelineCheck(w.week_start).catch(() => null)));
      const rows = [];
      checks.forEach((g, idx) => {
        if (!g) return;
        const weekStart = recent[idx].week_start;
        g.week_issues.forEach(issue => {
          rows.push({ warningType: 'week', scopeKey: issue.scope_key, message: issue.message, context: `Week van ${fmtDate(weekStart)}`, ...issue });
        });
        g.days.forEach(day => {
          day.issues.forEach(issue => {
            if (issue.kind === 'role_mismatch') return; // covered in the role-mismatch section below
            rows.push({ warningType: 'day_recipe', scopeKey: issue.scope_key, message: issue.message, context: `${issue.recipe_name} — ${fmtDate(day.date)}`, ...issue });
          });
        });
      });
      setGuidelineRows(rows);
    }).catch(e => setError(e.message || 'Laden mislukt.'));

    setRoleRows(null);
    getRecipes().then(recipes => {
      const rows = [];
      recipes.forEach(r => (r.role_warnings || []).forEach(w => {
        rows.push({ warningType: 'role_mismatch', scopeKey: w.scope_key, message: w.message, context: r.name, ...w });
      }));
      setRoleRows(rows);
    }).catch(() => setRoleRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const renderSection = (title, rows) => {
    if (rows === null) return <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Loading…</div>;
    const unapproved = rows.filter(r => !r.approved);
    const approved = rows.filter(r => r.approved);
    return (
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{title} {rows.length > 0 && <span style={{ color: 'var(--text-dimmed)', fontWeight: 400 }}>({unapproved.length} open, {approved.length} goedgekeurd)</span>}</div>
        {rows.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>Geen waarschuwingen.</div>}
        {[...unapproved, ...approved].map((r, i) => (
          <ApprovalRow key={`${r.warningType}:${r.scopeKey}:${i}`} warningType={r.warningType} scopeKey={r.scopeKey} message={r.message}
            context={r.context} approved={r.approved} approvedBy={r.approved_by} approvedAt={r.approved_at} user={user} onChanged={load} />
        ))}
      </div>
    );
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalBox, maxWidth: 520 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '14px 18px 0' }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Waarschuwingen goedkeuren</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', padding: '0 18px 14px' }}>
          Goedkeuren verandert geen recept-, classificatie- of richtlijnendata — het is puur een gemarkeerde uitzondering, zichtbaar in het planningsoverzicht.
        </div>
        <div style={{ padding: '0 18px 18px' }}>
          {error && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 10 }}>{error}</div>}
          {renderSection('Richtlijnen-overtredingen', guidelineRows)}
          {renderSection('Rol-mismatches', roleRows)}
        </div>
      </div>
    </div>
  );
}

const SCOOP_ROLE_LABELS = { protein: 'Eiwitbron', carbs: 'Koolhydraatbron', vegetable: 'Groentebron', other: 'Overig' };

// Mirrors server.staging.js's scoopGroupKey — a scoop group's identity is its member roles,
// sorted and joined, independent of unit_name/scoop_ml.
const scoopGroupKey = (roles) => [...roles].sort().join('+');

// Admin screen for a recipe's scoop-group setup — dish-first: pick ONE recipe from a dropdown,
// then edit that dish's own groups (which scoop + its real weight). GroupScoopEditor below
// AUTOMATICALLY resolves the weight for a newly-picked scoop size from any other recipe/scoop
// size that was weighed for this exact same set of products (server.staging.js's
// computeGroupPerCount uses the same density lookup) — it's a real auto-fill, not just a
// suggestion to tweak; saving an explicit value for this specific recipe+group still always
// overrides it. Lives on the recipe itself (nutrition_recipes.scoop_groups), normally set while
// editing that recipe in RecipeEditor.js (available to chef AND admin) — this is the second
// access point, across the whole recipe library without opening each recipe individually.
function ScoopGramsEditor({ user, onClose }) {
  const [recipes, setRecipes] = useState(null);
  const [selectedId, setSelectedId] = useState('');

  const load = useCallback(() => { getScoopMlOverview().then(setRecipes).catch(() => setRecipes([])); }, []);
  useEffect(() => { load(); }, [load]);

  const selected = recipes && recipes.find(r => r.id === Number(selectedId));

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalBox, maxWidth: 480 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Opscheplepel-instellingen</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 12 }}>
          Kies een gerecht, en stel per opscheplepel-groep de scoopmaat en het gewicht in. Is
          dezelfde combinatie producten al eens bij één scoopmaat gewogen (in dit of een ander
          recept), dan wordt het gewicht voor elke scoopmaat automatisch ingevuld. Zonder ooit
          gewogen gewicht telt de scoopmaat voorlopig rechtstreeks als gram.
        </div>
        {!recipes && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
        {recipes && !recipes.length && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>Nog geen recepten met een opscheplepel-groep.</div>}
        {recipes && recipes.length > 0 && (
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)} style={{ ...modalInput, width: '100%', marginBottom: 12, display: 'block' }}>
            <option value="">— kies een gerecht —</option>
            {recipes.map(r => <option key={r.id} value={r.id}>{r.name} ({r.md_label})</option>)}
          </select>
        )}
        {selected && selected.groups.map(g => (
          <GroupScoopEditor key={scoopGroupKey(g.roles)} recipeId={selected.id} group={g} user={user} onSaved={load} />
        ))}
      </div>
    </div>
  );
}

// One scoop-group's editor within ScoopGramsEditor above: pick the physical scoop, then its
// real weight for THIS recipe+group specifically. Picking a scoop size this exact set of
// products (group.food_ids) has ever been weighed at — in THIS recipe or any other — AUTO-FILLS
// the weight from that density; saving a different value for this specific recipe+group still
// overrides it (computeGroupPerCount's priority order).
function GroupScoopEditor({ recipeId, group, user, onSaved }) {
  const groupKey = scoopGroupKey(group.roles);
  const roleNames = group.roles.map(role => SCOOP_ROLE_LABELS[role]).join(' + ');
  const [scoopMl, setScoopMl] = useState(group.scoop_ml || null);
  const [grams, setGrams] = useState(group.scoop_grams != null ? String(group.scoop_grams) : '');
  const [resolved, setResolved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Re-sync if this group's saved state changed from outside (e.g. after Save reloads the
  // whole dish list) — otherwise this row would keep showing whatever it had before the reload.
  useEffect(() => {
    setScoopMl(group.scoop_ml || null);
    setGrams(group.scoop_grams != null ? String(group.scoop_grams) : '');
    setResolved(null);
  }, [group.scoop_ml, group.scoop_grams]);

  const pickScoop = async (ml) => {
    setMsg(null);
    if (ml === scoopMl) { setScoopMl(null); setGrams(''); setResolved(null); return; }
    setScoopMl(ml);
    // Switching to the size this recipe's group ALREADY has its own saved weight for shows
    // that stored value directly — no need to re-resolve it.
    if (ml === group.scoop_ml && group.scoop_grams != null) {
      setGrams(String(group.scoop_grams)); setResolved(null); return;
    }
    setGrams(''); setResolved(null);
    if (group.food_ids && group.food_ids.length) {
      try {
        const { grams: resolvedGrams } = await getScoopWeightSuggestion(group.food_ids, ml);
        if (resolvedGrams != null) { setResolved(resolvedGrams); setGrams(String(resolvedGrams)); }
      } catch { /* never weighed for this exact product set yet — leave blank */ }
    }
  };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const gramsValue = grams.trim() === '' ? null : Number(grams);
      await updateScoopMlOverview(recipeId, groupKey, scoopMl, gramsValue, user?.role, user?.id);
      setMsg({ ok: true, text: 'Opgeslagen.' });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Opslaan mislukt.' });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
        {roleNames}
        {group.food_names && <span style={{ color: 'var(--text-dimmed)' }}> — op basis van {group.food_names}</span>}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
        {SCOOP_SIZES.map(({ ml, name }) => (
          <button key={ml} disabled={saving} style={{ ...btn(scoopMl === ml), padding: '3px 8px', fontSize: 11 }} onClick={() => pickScoop(ml)}>
            {name} <span style={{ opacity: 0.75 }}>({ml} ml)</span>
          </button>
        ))}
      </div>
      {scoopMl && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <input type="number" min="0" step="0.1" placeholder={`nog niet ingevuld (${scoopMl}g)`}
            value={grams} onChange={e => setGrams(e.target.value)} style={{ ...modalInput, maxWidth: 90 }} disabled={saving} />
          <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>g</span>
          {resolved != null && Number(grams) === resolved && (
            <span style={{ fontSize: 10, color: '#10b981' }}>automatisch ingevuld — eerder gewogen</span>
          )}
        </div>
      )}
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button style={{ ...btn(true), padding: '3px 9px', fontSize: 11 }} disabled={saving} onClick={save}>{saving ? '…' : 'Save'}</button>
        {msg && <span style={{ fontSize: 11, color: msg.ok ? '#10b981' : '#ef4444' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

const PROTEIN_TYPE_OPTIONS = [
  { key: 'vis', label: 'Vis' },
  { key: 'tofu_tempeh', label: 'Tofu/tempeh' },
  { key: 'rood_vlees', label: 'Rood vlees' },
  { key: 'kip', label: 'Kip' },
];

// Search a food (existing FoodAutocomplete) and assign its plant-family/species and/or protein
// category — prefilled from whatever's already saved for it, so re-picking a food doubles as
// "review what this one is currently tagged as".
function ClassifyFoodPanel({ user }) {
  const [food, setFood] = useState(null);
  const [species, setSpecies] = useState([]);
  const [speciesId, setSpeciesId] = useState('');
  const [proteinType, setProteinType] = useState('');
  const [isCarbsSource, setIsCarbsSource] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { getVegSpecies().then(setSpecies).catch(() => setSpecies([])); }, []);

  const pick = async (f) => {
    setFood(f); setMsg(null); setLoading(true);
    try {
      const cur = await getFoodClassification(f.id);
      setSpeciesId(cur.veg ? String(cur.veg.species_id) : '');
      setProteinType(cur.protein_type || '');
      setIsCarbsSource(!!cur.is_carbs_source);
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!food) return;
    setSaving(true); setMsg(null);
    try {
      await setFoodClassification(food.id, { species_id: speciesId ? Number(speciesId) : null, protein_type: proteinType || null, is_carbs_source: isCarbsSource }, user?.role);
      setMsg({ ok: true, text: 'Opgeslagen.' });
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Opslaan mislukt.' });
    } finally { setSaving(false); }
  };

  const nonExcluded = species.filter(s => !s.excluded);

  return (
    <div>
      <FoodAutocomplete onPick={pick} placeholder="Zoek een voedingsmiddel…" />
      {food && (
        <div style={{ marginTop: 12, background: 'var(--bg-app)', borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{food.name}</div>
          {loading ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div> : (
            <>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Groentesoort</label>
              <select value={speciesId} onChange={e => setSpeciesId(e.target.value)} style={{ ...modalInput, width: '100%', marginBottom: 10, display: 'block' }}>
                <option value="">— geen —</option>
                {nonExcluded.map(s => <option key={s.id} value={s.id}>{s.name} ({s.family})</option>)}
              </select>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>Eiwitcategorie</label>
              <select value={proteinType} onChange={e => setProteinType(e.target.value)} style={{ ...modalInput, width: '100%', marginBottom: 10, display: 'block' }}>
                <option value="">— geen —</option>
                {PROTEIN_TYPE_OPTIONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <input type="checkbox" checked={isCarbsSource} onChange={e => setIsCarbsSource(e.target.checked)} /> Koolhydraatbron
              </label>
              <button style={btn(true)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Opslaan'}</button>
              {msg && <span style={{ fontSize: 11, marginLeft: 8, color: msg.ok ? '#10b981' : '#ef4444' }}>{msg.text}</span>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// The species vocabulary itself (name + family, or "excluded" for Ui/Taugé) — separate from
// assigning a species to a specific food (ClassifyFoodPanel above).
// A row being renamed/re-familied in place — Save uses updateVegSpecies so its food associations
// (nutrition_food_veg_species) survive; deleting and re-adding a species under a new name would
// cascade-delete every food already tagged with it, which a rename should never do.
function SpeciesRow({ s, user, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name);
  const [family, setFamily] = useState(s.family || VEG_FAMILIES[0].name);
  const [excluded, setExcluded] = useState(s.excluded);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true); setErr('');
    try {
      const updated = await updateVegSpecies(s.id, { name: name.trim(), family: excluded ? null : family, excluded }, user?.role);
      onSaved(updated);
      setEditing(false);
    } catch (e) { setErr(e.message || 'Opslaan mislukt.'); } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
        <span>{s.name} <span style={{ color: 'var(--text-dimmed)' }}>{s.excluded ? '(telt niet mee)' : s.family ? `— ${s.family}` : ''}</span></span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setEditing(true)} style={{ ...btn(false), padding: '2px 6px' }}>Bewerk</button>
          <button onClick={() => onDeleted(s.id)} style={{ ...btn(false, '#ef4444'), padding: '2px 6px' }}><Trash2 size={11} /></button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
      <input value={name} onChange={e => setName(e.target.value)} style={{ ...modalInput, minWidth: 90 }} />
      {!excluded && (
        <select value={family} onChange={e => setFamily(e.target.value)} style={modalInput}>
          {VEG_FAMILIES.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
        </select>
      )}
      <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
        <input type="checkbox" checked={excluded} onChange={e => setExcluded(e.target.checked)} /> telt niet mee
      </label>
      <button style={btn(true)} onClick={save} disabled={saving}>{saving ? '…' : 'Save'}</button>
      <button style={btn(false)} onClick={() => setEditing(false)}>Cancel</button>
      {err && <span style={{ fontSize: 11, color: '#ef4444' }}>{err}</span>}
    </div>
  );
}

function SpeciesManagerPanel({ user }) {
  const [species, setSpecies] = useState(null);
  const [newName, setNewName] = useState('');
  const [newFamily, setNewFamily] = useState(VEG_FAMILIES[0].name);
  const [newExcluded, setNewExcluded] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => { getVegSpecies().then(setSpecies).catch(() => setSpecies([])); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    setMsg(null);
    try {
      await createVegSpecies({ name: newName.trim(), family: newExcluded ? null : newFamily, excluded: newExcluded }, user?.role);
      setNewName(''); load();
    } catch (e) { setMsg({ ok: false, text: e.message || 'Toevoegen mislukt.' }); }
  };
  const remove = async (id) => {
    try { await deleteVegSpecies(id, user?.role); load(); }
    catch (e) { setMsg({ ok: false, text: e.message || 'Verwijderen mislukt.' }); }
  };
  const saved = (updated) => setSpecies(list => list.map(x => x.id === updated.id ? updated : x));

  if (!species) return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nieuwe soort…" style={{ ...modalInput, minWidth: 100 }} />
        {!newExcluded && (
          <select value={newFamily} onChange={e => setNewFamily(e.target.value)} style={modalInput}>
            {VEG_FAMILIES.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
          </select>
        )}
        <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={newExcluded} onChange={e => setNewExcluded(e.target.checked)} /> telt niet mee
        </label>
        <button style={btn(false)} onClick={add}>Toevoegen</button>
      </div>
      {msg && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>{msg.text}</div>}
      {species.map(s => (
        <SpeciesRow key={s.id} s={s} user={user} onSaved={saved} onDeleted={remove} />
      ))}
    </div>
  );
}

// Review everything already classified (auto-seeded or admin-assigned) so an admin can correct
// the seed instead of only ever tagging blind via search.
function ClassificationReviewPanel({ user }) {
  const [type, setType] = useState('veg');
  const [list, setList] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => { setList(null); getClassificationList(type).then(setList).catch(() => setList([])); }, [type]);
  useEffect(() => { load(); }, [load]);

  const clearField = { veg: { species_id: null }, protein: { protein_type: null }, carbs: { is_carbs_source: false } }[type];
  const clear = async (foodId) => {
    try {
      await setFoodClassification(foodId, clearField, user?.role);
      load();
    } catch (e) { setMsg({ ok: false, text: e.message || 'Verwijderen mislukt.' }); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button style={btn(type === 'veg')} onClick={() => setType('veg')}>Groenten</button>
        <button style={btn(type === 'protein')} onClick={() => setType('protein')}>Eiwit</button>
        <button style={btn(type === 'carbs')} onClick={() => setType('carbs')}>Koolhydraten</button>
      </div>
      {msg && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>{msg.text}</div>}
      {!list && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
      {list && list.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>Nog niets geclassificeerd.</div>}
      {list && list.map(row => (
        <div key={row.food_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
          <span>{row.name}{type !== 'carbs' && (
            <span style={{ color: 'var(--text-dimmed)' }}> — {type === 'veg' ? `${row.species} (${row.family})` : (PROTEIN_TYPE_OPTIONS.find(p => p.key === row.protein_type)?.label || row.protein_type)}</span>
          )}</span>
          <button onClick={() => clear(row.food_id)} style={{ ...btn(false, '#ef4444'), padding: '2px 6px' }}><Trash2 size={11} /></button>
        </div>
      ))}
    </div>
  );
}

// Admin-only diagnostic view over nutrition_search_log (scripts/nutrition_search_log.sql) —
// real search terms from the bulk-paste matcher and FoodAutocomplete, with what the system
// suggested vs what actually ended up chosen, so a systematic matching problem surfaces from
// real usage instead of only ever being found by hand via synthetic examples. "Alleen
// afwijkingen" is the fast path: a bulk-paste line matched to the SAME food it logged (no
// separate pick step, so those rows never count as a mismatch in that filter — only a true
// no-match, chosen_food_id null, counts there) vs an autocomplete pick that landed on something
// other than the #1 suggestion.
function SearchLogPanel() {
  const [mismatchOnly, setMismatchOnly] = useState(true);
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setRows(null);
    getSearchLog(mismatchOnly).then(setRows).catch(e => { setErr(e.message || 'Laden mislukt.'); setRows([]); });
  }, [mismatchOnly]);
  useEffect(() => { load(); }, [load]);

  const fmtTime = (iso) => new Date(iso).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="checkbox" checked={mismatchOnly} onChange={e => setMismatchOnly(e.target.checked)} /> alleen afwijkingen tonen
        </label>
        <button onClick={load} style={{ ...btn(false), padding: '4px 9px', fontSize: 11 }}>Vernieuwen</button>
      </div>
      {err && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>{err}</div>}
      {!rows && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</div>}
      {rows && rows.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>
          {mismatchOnly ? 'Geen afwijkingen gevonden.' : 'Nog niets gelogd.'}
        </div>
      )}
      {rows && rows.map(r => {
        const isMismatch = r.chosen_food_id !== r.top_suggestion_food_id;
        return (
          <div key={r.id} style={{ fontSize: 11, padding: '6px 4px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ fontWeight: 600 }}>"{r.search_term}"</span>
              <span style={{ color: 'var(--text-dimmed)', whiteSpace: 'nowrap' }}>
                {r.source === 'bulk_paste' ? 'plakken' : 'zoekbalk'} · {r.user_role || '?'} · {fmtTime(r.created_at)}
              </span>
            </div>
            <div style={{ color: 'var(--text-muted)' }}>
              suggestie: {r.top_suggestion_name || <em>geen match</em>}
              {r.source === 'autocomplete' && (
                <> &rarr; gekozen: <span style={{ color: isMismatch ? '#ef4444' : 'inherit', fontWeight: isMismatch ? 600 : 400 }}>{r.chosen_food_name || <em>geen</em>}</span></>
              )}
              {r.matched_via && <span style={{ color: 'var(--text-dimmed)' }}> ({r.matched_via})</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Admin-only: manage the veg-family/species, protein-type, and carbs-source classification the
// guideline check (veg/protein) and role auto-recognition (all three) rely on — auto-seeded, see
// scripts/nutrition_veg_protein_classification.sql and scripts/nutrition_carbs_classification.sql
// — an admin reviews/corrects it here, it's never assumed complete.
function ClassificationEditor({ user, onClose }) {
  const [tab, setTab] = useState('assign');
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalBox, maxWidth: tab === 'searchlog' ? 640 : 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>{tab === 'searchlog' ? 'Zoeklog (voedingsmiddelen)' : 'Groente-, eiwit- en koolhydraatindeling'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          <button style={btn(tab === 'assign')} onClick={() => setTab('assign')}>Toewijzen</button>
          <button style={btn(tab === 'species')} onClick={() => setTab('species')}>Soorten</button>
          <button style={btn(tab === 'review')} onClick={() => setTab('review')}>Overzicht</button>
          <button style={btn(tab === 'searchlog')} onClick={() => setTab('searchlog')}>Zoeklog</button>
        </div>
        {tab === 'assign' && <ClassifyFoodPanel user={user} />}
        {tab === 'species' && <SpeciesManagerPanel user={user} />}
        {tab === 'review' && <ClassificationReviewPanel user={user} />}
        {tab === 'searchlog' && <SearchLogPanel />}
      </div>
    </div>
  );
}

// Per-portion macro/veg badges — shared by the assigned-recipe card and the library picker, so
// a chef sees the same at-a-glance numbers whichever screen they're looking at. The 🥦 badge is
// only colored green when the day's actual Bord minimum is met — it used to be unconditionally
// green, which read as "requirement satisfied" even when a recipe was well under the minimum
// (e.g. 150 g on a 200 g/Bord 5 day); red now means "below the guideline for this Bord".
function MacroBadges({ perPortion, board }) {
  if (!perPortion) return null;
  const vegMin = board ? VEG_PER_BOARD_MIN_G[board] : null;
  const vegOk = vegMin == null || perPortion.veg_g >= vegMin;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
      <span>{perPortion.kcal} kcal</span>
      <span>P {perPortion.protein_g}g</span>
      <span>C {perPortion.carbs_g}g</span>
      <span>F {perPortion.fat_g}g</span>
      <span style={{ color: vegOk ? '#10b981' : '#ef4444', fontWeight: vegOk ? 400 : 700 }}>
        🥦 {perPortion.veg_g}g{vegMin != null && !vegOk ? ` (min. ${vegMin}g)` : ''}
      </span>
    </div>
  );
}

// One recipe already assigned to this day.
// issues: this specific recipe's guideline violations (from the day's recipe_issues map), or
// undefined/empty when it's clean — flagged red with an icon right here, not just discoverable
// by opening the check popup.
// onEdit: opens RecipeEditor for THIS recipe (fetched full, with ingredients) — undefined in
// read-only (admin) view, where the card stays inert like before.
function AssignedRecipeCard({ dayRecipe, board, readOnly, onUnassign, onEdit, loading, issues }) {
  const allIssues = issues || [];
  const unapproved = allIssues.filter(i => !i.approved);
  const hasIssues = unapproved.length > 0;
  const approvedOnly = !hasIssues && allIssues.length > 0;
  return (
    <div onClick={onEdit} title={onEdit ? 'Klik om te bewerken' : undefined}
      style={{
        background: hasIssues ? 'rgba(239,68,68,0.12)' : approvedOnly ? 'rgba(59,130,246,0.08)' : 'var(--bg-app)',
        border: hasIssues ? '1px solid #ef4444' : approvedOnly ? '1px solid #3b82f6' : '1px solid transparent',
        borderRadius: 8, padding: '8px 10px', marginTop: 6,
        cursor: onEdit ? 'pointer' : 'default',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
          {hasIssues && <AlertTriangle size={13} color="#ef4444" style={{ flexShrink: 0 }} />}
          {approvedOnly && <ShieldCheck size={13} color="#3b82f6" style={{ flexShrink: 0 }} title="Goedgekeurd door een admin" />}
          {loading ? 'Laden…' : dayRecipe.name}
        </div>
        {!readOnly && (
          <button style={{ ...btn(false, '#ef4444'), padding: '3px 6px', flexShrink: 0 }}
            onClick={e => { e.stopPropagation(); onUnassign(dayRecipe.day_recipe_id); }} title="Remove from this day">
            <X size={11} />
          </button>
        )}
      </div>
      {dayRecipe.meal_type && (
        <span style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-dimmed)' }}>
          {dayRecipe.meal_type}
        </span>
      )}
      <MacroBadges perPortion={dayRecipe.per_portion} board={board} />
      {allIssues.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {allIssues.map((issue, i) => (
            <div key={i} style={{ fontSize: 10, color: issue.approved ? '#3b82f6' : '#ef4444' }}>
              {issue.approved && <ShieldCheck size={9} style={{ marginRight: 3, verticalAlign: -1 }} />}
              {issue.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Standalone recipe-library view — a "New recipe" here just creates a library entry, no day
// assignment (the per-day "Add recipe" flow below still handles that, unchanged). Reachable via
// the Week/Recepten toggle at the top of ChefWeekPlanner.
const LIBRARY_MD_LABELS = ['MD-4', 'MD-3', 'MD-2', 'MD-1', 'MD', 'MD+1', 'MD+2', 'MD+3', 'MD+4'];

function RecipeLibraryView({ readOnly }) {
  const [mdFilter, setMdFilter] = useState('all');
  const [library, setLibrary] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | a full recipe object (with ingredients)
  const [loadingEditId, setLoadingEditId] = useState(null);

  const load = useCallback(() => {
    setLibrary(null);
    getRecipes(mdFilter === 'all' ? undefined : mdFilter).then(setLibrary).catch(() => setLibrary([]));
  }, [mdFilter]);
  useEffect(() => { load(); }, [load]);

  // The list above has no `ingredients` (only per_portion/role_warnings, kept lean since most
  // rows are never opened) — editing needs the full recipe, fetched on demand right here.
  const startEdit = async (recipeId) => {
    setLoadingEditId(recipeId);
    try { setEditing(await getRecipe(recipeId)); }
    finally { setLoadingEditId(null); }
  };

  if (editing) {
    return (
      <RecipeEditor
        initial={editing === 'new' ? null : editing}
        defaultMdLabel={mdFilter !== 'all' ? mdFilter : 'MD'}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select value={mdFilter} onChange={e => setMdFilter(e.target.value)} style={{ padding: '6px 9px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'inherit' }}>
          <option value="all">Alle Borden</option>
          {LIBRARY_MD_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {!readOnly && (
          <button style={{ ...btn(true), marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setEditing('new')}>
            <Plus size={13} /> New recipe
          </button>
        )}
      </div>

      {library === null && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>}
      {library && library.length === 0 && <div style={{ ...card, color: 'var(--text-dimmed)', fontSize: 12 }}>No recipes yet.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {library && library.map(r => {
          const roleWarnings = r.role_warnings || [];
          const unapprovedRoleWarnings = roleWarnings.filter(w => !w.approved);
          const hasRoleWarnings = unapprovedRoleWarnings.length > 0;
          const approvedOnly = !hasRoleWarnings && roleWarnings.length > 0;
          const loading = loadingEditId === r.id;
          return (
            <div key={r.id} onClick={!readOnly ? () => startEdit(r.id) : undefined}
              title={!readOnly ? 'Klik om te bewerken' : undefined}
              style={{
                ...card, marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10,
                cursor: !readOnly ? 'pointer' : 'default',
                ...(hasRoleWarnings ? { border: '1px solid #f59e0b', background: 'rgba(245,158,11,0.08)' } : {}),
                ...(approvedOnly ? { border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.06)' } : {}),
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {hasRoleWarnings && <AlertTriangle size={13} color="#f59e0b" title="Rol per ingrediënt komt niet overeen met de database-classificatie" />}
                  {approvedOnly && <ShieldCheck size={13} color="#3b82f6" title="Rol-mismatch goedgekeurd door een admin" />}
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{loading ? 'Laden…' : r.name}</span>
                  <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 99, background: 'var(--bg-app)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{r.md_label}</span>
                </div>
                <MacroBadges perPortion={r.per_portion} />
              </div>
              {!readOnly && (
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => startEdit(r.id)} style={{ ...btn(false), padding: '4px 9px' }}>Edit</button>
                  <button onClick={async () => { await deleteRecipe(r.id); load(); }} style={{ ...btn(false, '#ef4444'), padding: '4px 8px' }}><Trash2 size={12} /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Popover for adding a recipe to a day: pick from the library (filtered to the day's own
// label when scheduled — an unscheduled day has no label to filter by, so the whole library
// shows instead) or create a brand new one, which gets assigned to this day as soon as it's
// saved.
// Same enum as the eetmeter tabs (server.staging.js's NUTRITION_MEALS) — required before a
// recipe can be assigned to a day, so a player's logged scoop/unit count for it can later be
// filed under the right meal in their own Food Tracker (see computeLoggedScoopEntries).
const MEAL_TYPE_OPTIONS = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
  { key: 'snack', label: 'Snack' },
];

function AddRecipeMenu({ day, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [library, setLibrary] = useState(null);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [mealType, setMealType] = useState(null);
  // Set when the server rejects an assignment — most commonly the "every scoop group needs a
  // size + weight before it can go on the week menu" check (server.staging.js's POST
  // day-recipes), which otherwise fails silently (assign() had no error handling at all before).
  const [assignError, setAssignError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLibrary(null);
    setMealType(null);
    setAssignError('');
    getRecipes(day.scheduled ? day.label : undefined).then(setLibrary).catch(() => setLibrary([]));
  }, [open, day.scheduled, day.label]);

  const assign = async (recipeId) => {
    if (!mealType) return;
    setAssigning(recipeId); setAssignError('');
    try {
      await assignDayRecipe({ date: day.date, recipe_id: recipeId, meal_type: mealType });
      setOpen(false); onAssigned();
    } catch (e) {
      setAssignError(e.message || 'Toevoegen mislukt.');
    } finally { setAssigning(null); }
  };

  if (creating) {
    return (
      <RecipeEditor
        defaultMdLabel={day.scheduled ? day.label : 'MD'}
        onCancel={() => setCreating(false)}
        onSaved={async (recipe) => { await assign(recipe.id); setCreating(false); }}
      />
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <button style={{ ...btn(false), marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => setOpen(o => !o)}>
        <Plus size={12} /> Add recipe
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 30, marginTop: 4, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, width: 240, maxHeight: 320, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', padding: 8 }}>
          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', padding: '2px 4px 4px' }}>Meal moment</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '0 4px 8px' }}>
            {MEAL_TYPE_OPTIONS.map(m => (
              <button key={m.key} type="button" style={{ ...btn(mealType === m.key), padding: '4px 8px', fontSize: 11 }} onClick={() => setMealType(m.key)}>
                {m.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', padding: '2px 4px 6px' }}>
            {day.scheduled ? `Recipes for ${day.label}` : 'All recipes (day is unscheduled)'}
          </div>
          {!mealType && <div style={{ fontSize: 11, color: '#f59e0b', padding: '0 4px 8px' }}>Pick a meal moment first.</div>}
          {assignError && <div style={{ fontSize: 11, color: '#ef4444', padding: '0 4px 8px' }}>{assignError}</div>}
          {library === null && <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 6 }}>Loading…</div>}
          {library && library.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)', padding: 6 }}>No recipes yet for this Bord.</div>}
          {library && library.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 2, borderRadius: 6 }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-app)'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <button type="button" onClick={() => assign(r.id)} disabled={assigning === r.id || !mealType}
                style={{ display: 'block', flex: 1, minWidth: 0, textAlign: 'left', padding: '6px 6px', background: 'none', border: 'none', color: 'var(--text-primary)', cursor: mealType ? 'pointer' : 'not-allowed', fontFamily: 'inherit', opacity: mealType ? 1 : 0.5 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{assigning === r.id ? 'Adding…' : r.name}</div>
                {!day.scheduled && <div style={{ fontSize: 9, color: 'var(--text-dimmed)' }}>{r.md_label}</div>}
                <MacroBadges perPortion={r.per_portion} board={day.scheduled ? day.board : null} />
              </button>
              <button type="button" title="Delete recipe from library"
                onClick={async () => { await deleteRecipe(r.id); setLibrary(list => list.filter(x => x.id !== r.id)); }}
                style={{ background: 'none', border: 'none', color: 'var(--text-dimmed)', cursor: 'pointer', padding: 6, flexShrink: 0 }}>
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setCreating(true)} disabled={!mealType}
            style={{ ...btn(false), width: '100%', marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, opacity: mealType ? 1 : 0.5, cursor: mealType ? 'pointer' : 'not-allowed' }}>
            <BookOpen size={12} /> New recipe
          </button>
        </div>
      )}
    </div>
  );
}

// One calendar day's column — "Tuesday 26 Aug" with an "MD-2 — Bord 5" badge underneath when
// Week Schedule has actually classified that date as matchday-related, or "Unscheduled" (and
// the same light-grey card Week Schedule itself uses) when it hasn't.
// dayGuideline: {issues, recipe_issues} for this date from the week's guideline check (or
// undefined while still loading) — drives the passive red flagging below, visible immediately in
// the grid rather than only after opening the check popup.
function DayColumn({ dayIndex, day, readOnly, onChanged, onCheckDay, onAutoCheckDay, dayGuideline }) {
  const unassign = async (dayRecipeId) => { await unassignDayRecipe(dayRecipeId); onChanged(); onAutoCheckDay(day.date); };
  const [editingBoard, setEditingBoard] = useState(false);
  const allDayIssues = dayGuideline?.issues || [];
  const hasIssues = allDayIssues.some(i => !i.approved);
  const approvedOnly = !hasIssues && allDayIssues.length > 0;
  const recipeIssues = dayGuideline?.recipe_issues || {};

  // Editing here (as opposed to the standalone library view) needs the full recipe — the day's
  // own list only carries per_portion/name — fetched on demand so a red-flagged card can
  // actually be opened and fixed, not just seen.
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [loadingEditId, setLoadingEditId] = useState(null);
  const startEdit = async (recipeId) => {
    setLoadingEditId(recipeId);
    try { setEditingRecipe(await getRecipe(recipeId)); }
    finally { setLoadingEditId(null); }
  };

  if (editingRecipe) {
    return (
      <RecipeEditor
        initial={editingRecipe}
        onCancel={() => setEditingRecipe(null)}
        onSaved={() => { setEditingRecipe(null); onChanged(); onAutoCheckDay(day.date); }}
      />
    );
  }

  return (
    <div style={{
      ...card, marginBottom: 0, flex: '1 1 200px', minWidth: 200,
      ...(day.scheduled ? {} : UNSCHEDULED_CARD),
      ...(hasIssues ? { background: 'rgba(239,68,68,0.10)', border: '1px solid #ef4444' } : {}),
      ...(approvedOnly ? { background: 'rgba(59,130,246,0.06)', border: '1px solid #3b82f6' } : {}),
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5 }}>
          {hasIssues && <AlertTriangle size={14} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />}
          {approvedOnly && <ShieldCheck size={14} color="#3b82f6" style={{ flexShrink: 0, marginTop: 2 }} title="Goedgekeurd door een admin" />}
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{DAY_NAMES[dayIndex]}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, marginBottom: 4 }}>{fmtDate(day.date)}</div>
          </div>
        </div>
        {day.recipes.length > 0 && (
          <button title="Check richtlijnen voor deze dag" onClick={() => onCheckDay(day.date)}
            style={{ background: 'none', border: 'none', color: hasIssues ? '#ef4444' : approvedOnly ? '#3b82f6' : 'var(--text-dimmed)', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
            {hasIssues ? <AlertTriangle size={14} /> : approvedOnly ? <ShieldCheck size={14} /> : <ClipboardCheck size={14} />}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 99,
          background: 'var(--bg-app)', border: `1px solid ${day.board_override ? '#f59e0b' : 'var(--border)'}`, color: 'var(--text-secondary)',
        }} title={day.board_override ? 'Eenmalige correctie voor deze dag' : undefined}>
          {day.scheduled ? `${day.label}${day.board ? ` — ${day.board}` : ''}` : 'Unscheduled'}
        </span>
        {!readOnly && day.scheduled && (
          <button title="Bord voor deze dag corrigeren" onClick={() => setEditingBoard(true)}
            style={{ background: 'none', border: 'none', color: 'var(--text-dimmed)', cursor: 'pointer', padding: 0 }}>
            <Settings size={11} />
          </button>
        )}
      </div>
      {editingBoard && (
        <BoardOverrideEditor date={day.date} currentBoard={day.board} overrideBoard={day.board_override}
          onClose={() => setEditingBoard(false)} onSaved={onChanged} />
      )}

      {day.recipes.length === 0 && (
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>No recipes yet.</div>
      )}
      {day.recipes.map(r => (
        <AssignedRecipeCard key={r.day_recipe_id} dayRecipe={r} board={day.board} readOnly={readOnly} onUnassign={unassign}
          onEdit={!readOnly ? () => startEdit(r.id) : undefined} loading={loadingEditId === r.id}
          issues={recipeIssues[r.day_recipe_id]} />
      ))}
      {!readOnly && <AddRecipeMenu day={day} onAssigned={() => { onChanged(); onAutoCheckDay(day.date); }} />}
    </div>
  );
}

export default function ChefWeekPlanner({ readOnly = false, user }) {
  const [view, setView] = useState('week'); // 'week' | 'library'
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayStr()));
  const [days, setDays] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [guideline, setGuideline] = useState(null); // full {days, week_issues} for weekStart — drives the passive red flagging in the grid
  const [guidelineCheck, setGuidelineCheck] = useState(null); // {scope:'day'|'week', date?} — which modal is open, if any
  const [showBoardMap, setShowBoardMap] = useState(false);
  const [showClassification, setShowClassification] = useState(false);
  const [showScoopGrams, setShowScoopGrams] = useState(false);
  const [showWarningApprovals, setShowWarningApprovals] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(() => {
    setDays(null);
    getChefRecipes(weekStart).then(d => setDays(d.days || [])).catch(() => setDays([]));
    getChefWeeks().then(setWeeks).catch(() => {});
    getGuidelineCheck(weekStart).then(setGuideline).catch(() => setGuideline(null));
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  // Fired right after a day's recipes change (assign/unassign) — refreshes the guideline state
  // (so the grid's passive red flagging stays current) and only pops the modal open when that
  // SPECIFIC day has a violation, so saving a day that's already fine doesn't interrupt the chef
  // with a "well done" dialog every time.
  const autoCheckDay = useCallback(async (date) => {
    try {
      const g = await getGuidelineCheck(weekStart);
      setGuideline(g);
      const day = g.days.find(d => d.date === date);
      if (day && day.issues.some(i => !i.approved)) setGuidelineCheck({ scope: 'day', date });
    } catch { /* a failed background check shouldn't interrupt the save flow */ }
  }, [weekStart]);

  const isCurrentWeek = weekStart === mondayOf(todayStr());
  const guidelineByDate = {};
  (guideline?.days || []).forEach(d => { guidelineByDate[d.date] = d; });
  const allWeekIssues = [...(guideline?.week_issues || []), ...(guideline?.days || []).flatMap(d => d.issues)];
  const hasUnapprovedWeekIssues = allWeekIssues.some(i => !i.approved);
  const weekApprovedOnly = !hasUnapprovedWeekIssues && allWeekIssues.length > 0;

  return (
    <div>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button style={btn(view === 'week')} onClick={() => setView('week')}>Week</button>
        <button style={btn(view === 'library')} onClick={() => setView('library')}>Recepten</button>
      </div>

      {view === 'library' && <RecipeLibraryView readOnly={readOnly} />}

      {view === 'week' && (
      <>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setWeekStart(w => addDays(w, -7))}><ChevronLeft size={15} /></button>
        <div style={{ fontSize: 13, fontWeight: 600, minWidth: 170, textAlign: 'center' }}>{fmtWeekRange(weekStart)}</div>
        <button style={{ ...btn(false), padding: 6 }} onClick={() => setWeekStart(w => addDays(w, 7))}><ChevronRight size={15} /></button>
        <button style={btn(isCurrentWeek)} onClick={() => setWeekStart(mondayOf(todayStr()))}>This week</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={{ ...btn(hasUnapprovedWeekIssues, hasUnapprovedWeekIssues ? '#ef4444' : '#3b82f6'), ...(weekApprovedOnly ? { border: '1px solid #3b82f6', color: '#3b82f6' } : {}), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setGuidelineCheck({ scope: 'week' })}>
            {hasUnapprovedWeekIssues ? <AlertTriangle size={13} /> : weekApprovedOnly ? <ShieldCheck size={13} /> : <ClipboardCheck size={13} />} Check richtlijnen
          </button>
          {isAdmin && (
            <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setShowBoardMap(true)}>
              <Settings size={13} /> Scenario-bordkoppeling
            </button>
          )}
          {isAdmin && (
            <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setShowClassification(true)}>
              <Tag size={13} /> Groente/eiwit/koolhydraat-indeling
            </button>
          )}
          {isAdmin && (
            <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setShowScoopGrams(true)}>
              <Settings size={13} /> Opscheplepel-instellingen
            </button>
          )}
          {isAdmin && (
            <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setShowWarningApprovals(true)}>
              <ShieldCheck size={13} /> Waarschuwingen goedkeuren
            </button>
          )}
          <ChefMealGuidelines />
          <div style={{ position: 'relative' }}>
            <button style={{ ...btn(false), display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setShowHistory(s => !s)}>
              <History size={13} /> Previous weeks {weeks.length > 0 && `(${weeks.length})`}
            </button>
            {showHistory && (
              <div style={{ position: 'absolute', top: '110%', right: 0, zIndex: 20, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, minWidth: 220, maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                {weeks.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)', padding: 10 }}>No weeks yet.</div>}
                {weeks.map(w => (
                  <button key={w.week_start} type="button" onClick={() => { setWeekStart(w.week_start); setShowHistory(false); }}
                    style={{ display: 'flex', justifyContent: 'space-between', width: '100%', textAlign: 'left', padding: '8px 12px', background: w.week_start === weekStart ? 'var(--bg-app)' : 'none', border: 'none', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <span>{fmtWeekRange(w.week_start)}</span>
                    <span style={{ color: 'var(--text-dimmed)' }}>{w.recipe_count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {days === null ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {days.map((day, i) => (
            <DayColumn key={day.date} dayIndex={i} day={day} readOnly={readOnly} onChanged={load}
              dayGuideline={guidelineByDate[day.date]}
              onCheckDay={date => setGuidelineCheck({ scope: 'day', date })} onAutoCheckDay={autoCheckDay} />
          ))}
        </div>
      )}

      {guidelineCheck && (
        <GuidelineModal weekStart={weekStart} scope={guidelineCheck.scope} date={guidelineCheck.date} onClose={() => setGuidelineCheck(null)} />
      )}
      {showBoardMap && <ScenarioBoardMapEditor user={user} onClose={() => setShowBoardMap(false)} />}
      {showClassification && <ClassificationEditor user={user} onClose={() => setShowClassification(false)} />}
      {showScoopGrams && <ScoopGramsEditor user={user} onClose={() => setShowScoopGrams(false)} />}
      {showWarningApprovals && (
        <WarningApprovalsPanel user={user} onClose={() => { setShowWarningApprovals(false); load(); }} />
      )}
      </>
      )}
    </div>
  );
}
